import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { useSubmitFeatureFeedback } from '../../../hooks/api/feedback/useSubmitFeatureFeedback'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SentimentSatisfiedRoundedIcon from '@mui/icons-material/SentimentSatisfiedRounded';
import { Star, XCircle } from "@phosphor-icons/react";

interface FeedbackDialogProps {
  labelType?:  "accepted" | "edited" | "denied";
  featureType: string;
  featureName: string;
  featureVersion: string;
  userId: string;
  entityType: string;
  entityId: number;
  requestId: string;
  clientId?: number;
  onClose: () => void;
}

function FeedbackDialog({
  labelType,
  featureType,
  featureName,
  featureVersion,
  userId,
  entityType,
  entityId,
  requestId,
  clientId, 
  onClose,
}: Readonly<FeedbackDialogProps>) {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string>("");
  const { submitFeedback } = useSubmitFeatureFeedback();

  const handleClose = () => {
    setOpen(false);
    setScore(null);
    setFeedback("");
    onClose();
  };

  const handleSubmit = () => {
    if (score === null) return;

    const data = {
      score,
      feedback: feedback.trim() || null,
      featureType,
      featureName,
      featureVersion,
      entityType,
      entityId,
      requestId,
      clientId,
    }

    submitFeedback({
      userId,
      data
    });

    handleClose();
  };

  const feedbackTextLabels = {
    accepted: {
      icon: <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />,
      title: "Quick feedback - What made this a helpful recommendation?",
      placeholder: "Why was this a helpful recommendation?"
    },
    edited: {
      icon: <SentimentSatisfiedRoundedIcon sx={{color: 'primary.main', fontSize: 20}} />,
      title: "Quick feedback - Why did you need to make these edits?",
      placeholder: "Your feedback helps us improve future recommendations."
    },
    denied: {
      icon: <XCircle size={20} color={theme.palette.error.main} />,
      title: "Help us improve - Why isn’t this useful?",
      placeholder: "Your feedback helps us improve future recommendations."
    }
  } as const;

  const feedbackTextLabel = feedbackTextLabels[labelType || "accepted"];
  return (
  <Dialog 
    open={open} 
    maxWidth="sm" 
    fullWidth
    onClose={(_event, reason) => {
      if (reason !== 'backdropClick') handleClose();
    }}
    disableEscapeKeyDown={true}
    aria-labelledby="feedback-dialog-title"
    hideBackdrop
    disableEnforceFocus
    disableAutoFocus
    disableScrollLock
    slotProps={{
      backdrop: {
        sx: { backdropFilter: 'none' },
      },
    }}
    PaperProps={{
      sx: { mb: 3, mr: 3 },
    }}
    sx={{
      zIndex: 2147483004,
      pointerEvents: 'none',
      '& .MuiDialog-container': {
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
      },
      '& .MuiPaper-root': {
      pointerEvents: 'all',
      },
    }}
    >
    <DialogTitle
      id="feedback-dialog-title"
      sx={{
        display: 'flex',
        padding: '16px 24px',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignSelf: 'stretch',
      }}
    >
      Your Feedback Matters
      <IconButton
        size="small"
        aria-label="close"
        onClick={handleClose}
        sx={{ color: 'text.secondary' }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb:'8px'}}>
        <Star aria-hidden="true" size={20} color="#F9A825" weight="duotone"/>
        <Typography variant="body2" id="rating-input-description">
          Rate the quality of this recommendation:
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', mb:'16px'}}>
        <Typography variant="body2">
          Poor
        </Typography>
        <RadioGroup 
          row 
          aria-labelledby="rating-input-description"
          name="rating-input"
          sx={{ gap: '24px' }}
        >
          {[1, 2, 3, 4, 5].map((value) => (   
            <FormControlLabel key={value} value={value} onClick={() => {setScore(value)}} control={<Radio />} label={value.toString()}/>
            )
          )}
        </RadioGroup>
        <Typography variant="body2">
          Excellent
        </Typography>
      </Box>
      <Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb:'4px'}}>
          {feedbackTextLabel.icon}
          <Typography variant="body2" id="feedback-text-label">{feedbackTextLabel.title}</Typography>
        </Box>
        <TextField
          aria-labelledby="feedback-text-label"
          fullWidth
          multiline
          minRows={4}
          maxRows={4}
          variant="outlined"
          placeholder={feedbackTextLabel.placeholder}
          sx={{ mt: 1 }}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        />
      </Box>
    </DialogContent>
     <DialogActions>
      <Button
       variant='contained'
       size='medium'
       color='primary'
       disabled={score === null}
       onClick={handleSubmit}
      >
        Submit
      </Button>
    </DialogActions>
  </Dialog>
  );
};

export default FeedbackDialog;
